import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from '@/authorization';
import { navigationNodeActionDefinitions } from '@/config/navigationConfig';
import type {
  NavigationNode,
  NavigationNodeActionId,
} from '@/types/navigationConfig';

interface NavigationContextMenuProps {
  node: NavigationNode;
  onAction: (actionId: NavigationNodeActionId, node: NavigationNode) => void;
}

export function NavigationContextMenu({
  node,
  onAction,
}: NavigationContextMenuProps) {
  const { t } = useTranslation('entity-portal');
  const { hasPermission } = useAuthorization();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const visibleActions = navigationNodeActionDefinitions.filter((action) => {
    if (!action.permission) {
      return true;
    }

    return hasPermission(action.permission);
  });

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (actionId: NavigationNodeActionId) => {
    onAction(actionId, node);
    handleClose();
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('navigationConfig.nodeActions', { node: node.id })}
        onClick={handleOpen}
        sx={{
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {visibleActions.map((action) => (
          <MenuItem
            key={action.id}
            onClick={() => handleSelect(action.id)}
            sx={
              'destructive' in action && action.destructive
                ? { color: 'error.main' }
                : undefined
            }
          >
            {t(action.labelKey)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
